import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Col, Row } from 'antd';

import { RiskScoreCard } from './RiskScoreCard';
import { RiskInfoCard } from './RiskInfoCard';
import { RiskLegend } from './RiskLegend';

import type { KimLhcRiskResult } from '@/types';
import { useGetSummaryMessages } from '@/components/views/Report/KimLhc/Report/Steps/Result/hooks/useGetSummaryMessages';
import * as S from '@/components/views/Report/KimLhc/Report/Steps/Result/styles';

interface SummaryProps {
	result: KimLhcRiskResult;
}

export function Summary({ result }: Readonly<SummaryProps>) {
	const hasSameRisk = result.manRiskLevel === result.womanRiskLevel;

	const {
		risk_level: man_risk_level,
		health_consequences: man_health_consequences,
		measures: man_measures
	} = useGetSummaryMessages(result.manRiskLevel);

	const {
		risk_level: woman_risk_level,
		health_consequences: woman_health_consequences,
		measures: woman_measures
	} = useGetSummaryMessages(result.womanRiskLevel);

	if (hasSameRisk) {
		return (
			<S.SectionContainer>
				<S.ScoresRow gutter={0}>
					<S.leftSummaryContainer span={12}>
						<RiskScoreCard
							title={I18n.get('Score for men')}
							score={result.manScore}
							riskLevel={result.manRiskLevel}
						/>
					</S.leftSummaryContainer>
					<S.rightSummaryContainer span={12}>
						<RiskScoreCard
							title={I18n.get('Score for women')}
							score={result.womanScore}
							riskLevel={result.womanRiskLevel}
						/>
					</S.rightSummaryContainer>
				</S.ScoresRow>
				<Row gutter={[24, 24]}>
					<Col xs={24} xl={8}>
						<RiskInfoCard title={I18n.get('Probability of physical overload')} text={woman_risk_level} />
					</Col>
					<Col xs={24} xl={8}>
						<RiskInfoCard
							title={I18n.get('Possible health consequences')}
							text={woman_health_consequences}
						/>
					</Col>
					<Col xs={24} xl={8}>
						<RiskInfoCard title={I18n.get('Measures')} text={woman_measures} />
					</Col>
				</Row>
				<RiskLegend />
			</S.SectionContainer>
		);
	}

	return (
		<S.SectionContainer>
			<S.VerticalRiskContainer>
				<S.RiskRow>
					<S.RiskScoreColumn>
						<RiskScoreCard
							title={I18n.get('Score for men')}
							score={result.manScore}
							riskLevel={result.manRiskLevel}
						/>
					</S.RiskScoreColumn>
					<S.RiskInfoColumns gutter={[24, 24]}>
						<Col xs={24} xl={8}>
							<RiskInfoCard title={I18n.get('Probability of physical overload')} text={man_risk_level} />
						</Col>
						<Col xs={24} xl={8}>
							<RiskInfoCard
								title={I18n.get('Possible health consequences')}
								text={man_health_consequences}
							/>
						</Col>
						<Col xs={24} xl={8}>
							<RiskInfoCard title={I18n.get('Measures')} text={man_measures} />
						</Col>
					</S.RiskInfoColumns>
				</S.RiskRow>
				<S.RiskRow>
					<S.RiskScoreColumn>
						<RiskScoreCard
							title={I18n.get('Score for women')}
							score={result.womanScore}
							riskLevel={result.womanRiskLevel}
						/>
					</S.RiskScoreColumn>
					<S.RiskInfoColumns gutter={[24, 24]}>
						<Col xs={24} xl={8}>
							<RiskInfoCard
								title={I18n.get('Probability of physical overload')}
								text={woman_risk_level}
							/>
						</Col>
						<Col xs={24} xl={8}>
							<RiskInfoCard
								title={I18n.get('Possible health consequences')}
								text={woman_health_consequences}
							/>
						</Col>
						<Col xs={24} xl={8}>
							<RiskInfoCard title={I18n.get('Measures')} text={woman_measures} />
						</Col>
					</S.RiskInfoColumns>
				</S.RiskRow>
			</S.VerticalRiskContainer>

			<RiskLegend />
		</S.SectionContainer>
	);
}
